import React, { useState } from "react";
import PropTypes from "prop-types";
import { ArrowRight, StarOff } from "lucide-react";
import { Collapse } from "@mui/material";

import { UIButton, UICard } from "../../ui";
import { clx } from "../../../utils/Helpers";
import DashboardEmptyState from "../DashboardEmptyState";
import SatisfactionCard from "./SatisfactionCard";
import useCardGridColumns from "../useCardGridColumns";
import "../dashboardSection.scss";
import "./SatisfactionSection.scss";

// Column count of a card grid (see `--satisfaction-columns` in the SCSS).
const columnsStyle = (count) => ({ "--satisfaction-columns": count });

/**
 * Shared "Client satisfaction" (company portal) / "Company Satisfaction" (admin)
 * dashboard section — a satisfaction card per evaluated client / company, or an
 * empty state. Wording differences are supplied by the composing page: the
 * `items` carry ready-built `subtitle` / `footerLabel` strings. The first row
 * (4 / 3 / 2 / 1 cards by viewport) shows; the rest reveal on "Show more" in
 * the same column sizes.
 */
const SatisfactionSection = ({
  title,
  overallLabel,
  bandLabels,
  items,
  viewAllLabel,
  onViewAll,
  showMoreLabel,
  showLessLabel,
  emptyTitle,
  emptyDescription,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const columns = useCardGridColumns();
  const hasMore = items.length > columns;
  // Both grids share one column count, so revealed cards keep the row's size.
  const gridStyle = columnsStyle(Math.min(items.length, columns));

  const action =
    viewAllLabel && onViewAll ? (
      <UIButton
        variant="text"
        className="dashboard-link"
        endIcon={<ArrowRight size={16} strokeWidth={2} />}
        onClick={onViewAll}
      >
        {viewAllLabel}
      </UIButton>
    ) : undefined;

  const renderCard = (item) => (
    <SatisfactionCard
      key={item.id}
      name={item.name}
      subtitle={item.subtitle}
      imageUrl={item.imageUrl}
      score={item.score}
      overallLabel={overallLabel}
      footerLabel={item.footerLabel}
      bandLabels={bandLabels}
    />
  );

  return (
    <UICard
      title={title}
      action={action}
      className={clx("dashboard-card satisfaction", className)}
    >
      {items.length === 0 ? (
        <DashboardEmptyState
          icon={<StarOff />}
          title={emptyTitle}
          description={emptyDescription}
        />
      ) : (
        <>
          <div className="satisfaction__grid" style={gridStyle}>
            {items.slice(0, columns).map(renderCard)}
          </div>

          {hasMore && (
            <>
              <Collapse in={expanded} timeout={320} unmountOnExit>
                <div
                  className="satisfaction__grid satisfaction__grid--rest"
                  style={gridStyle}
                >
                  {items.slice(columns).map(renderCard)}
                </div>
              </Collapse>
              <button
                type="button"
                className="satisfaction__toggle"
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? showLessLabel : showMoreLabel}
              </button>
            </>
          )}
        </>
      )}
    </UICard>
  );
};

SatisfactionSection.propTypes = {
  title: PropTypes.string.isRequired,
  overallLabel: PropTypes.string.isRequired,
  bandLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      subtitle: PropTypes.string.isRequired,
      score: PropTypes.number.isRequired,
      footerLabel: PropTypes.string,
    }),
  ),
  viewAllLabel: PropTypes.string,
  onViewAll: PropTypes.func,
  /** Required only when more items are passed than fit one row. */
  showMoreLabel: PropTypes.string,
  showLessLabel: PropTypes.string,
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
  className: PropTypes.string,
};

SatisfactionSection.defaultProps = {
  className: "",
  items: [],
  viewAllLabel: undefined,
  onViewAll: undefined,
  showMoreLabel: "",
  showLessLabel: "",
};

export default SatisfactionSection;
